/**
 * Handle specific Guidewire service API error codes
 * @param error - The error object from the API response
 * @returns {string} - User-friendly error message
 */
export const handleGuidewireError = (error) => {
	// Handle HTTP status code errors
	if (error.status === 400) {
		// Handle the specific error response structure from QuoteService
		if (error.data?.code === 'ENTITY_NOT_FOUND') {
			return "The quote you're looking for doesn't exist. Please check your quote ID and email address.";
		}
		if (error.data?.code === 'NOT_FOUND') {
			return "No quote found for the provided email and quote ID combination. Please verify your details and try again.";
		}
		if (error.data?.code === 'VALIDATION_ERROR') {
			return "Please check your information and try again. Some required fields may be missing or invalid.";
		}
		if (error.data?.code === 'INVALID_EMAIL') {
			return "Please provide a valid email address.";
		}
	}

	// Handle Guidewire specific error codes from sourceSystemError
	if (error.data?.sourceSystemError?.appErrorCode) {
		const appErrorCode = error.data.sourceSystemError.appErrorCode;

		switch (appErrorCode) {
			case 603:
				return "Quote not found. Please verify your details and try again.";
			case 604:
				return "Quote has expired. Please generate a new quote.";
			case 605:
				return "Quote is already processed. Please check your email for quote details.";
			case 606:
				return "Invalid quote status. Please try generating a new quote.";
			case 607:
				return "Quote creation failed. Please try again or contact support.";
			case 608:
				return "Service temporarily unavailable. Please try again in a few minutes.";
			case 609:
				return "Invalid animal data provided. Please check your livestock information.";
			case 610:
				return "Policyholder information is incomplete. Please fill in all required fields.";
			default:
				return `Service error (Code: ${appErrorCode}). Please contact Achmea support.`;
		}
	}

	// Handle legacy guidewireError structure (fallback)
	if (error.data?.guidewireError?.appErrorCode) {
		const appErrorCode = error.data.guidewireError.appErrorCode;

		switch (appErrorCode) {
			case 603:
				return "Quote not found. Please verify your details and try again.";
			case 604:
				return "Quote has expired. Please generate a new quote.";
			case 605:
				return "Quote is already processed. Please check your email for quote details.";
			case 606:
				return "Invalid quote status. Please try generating a new quote.";
			case 607:
				return "Quote creation failed. Please try again or contact support.";
			case 608:
				return "Service temporarily unavailable. Please try again in a few minutes.";
			case 609:
				return "Invalid animal data provided. Please check your livestock information.";
			case 610:
				return "Policyholder information is incomplete. Please fill in all required fields.";
			default:
				return `Service error (Code: ${appErrorCode}). Please contact Achmea support on 1800 724 214.`;
		}
	}

	// Handle network/connection errors
	if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
		return "Connection error. Please check your internet connection and try again or contact Achmea support on 1800 724 214.";
	}

	// Handle timeout errors
	if (error.code === 'TIMEOUT' || error.message?.includes('timeout')) {
		return "Request timed out. Please try again later or contact Achmea support on 1800 724 214.";
	}

	// Handle server errors
	if (error.status >= 500) {
		return "Server error. Please try again later or contact Achmea support on 1800 724 214.";
	}

	// Handle generic error messages from the API response
	// Prefer structured API fields if present
	const data = error?.data || error;
	const messages = [];

	// Common API message fields
	if (data?.message) messages.push(String(data.message));
	if (data?.description) messages.push(String(data.description));
	if (data?.detail) messages.push(String(data.detail));
	if (data?.error) messages.push(String(data.error));
	if (data?.title) messages.push(String(data.title));

	// Nested result message
	if (data?.result?.message) messages.push(String(data.result.message));

	// Validation style arrays: errors, details, messages
	const arrayish = [data?.errors, data?.details, data?.messages]
		.filter(Boolean)
		.flat()
		.map((e) => (typeof e === 'string' ? e : (e?.message || e?.description || JSON.stringify(e))))
		.filter(Boolean);
	if (arrayish.length) messages.push(...arrayish);

	// Handle specific error messages that indicate not-found
	const any = messages.join(' | ').toLowerCase();
	if (any.includes('entity not found') || any.includes('not found')) {
		return "No quote found for the provided email and quote ID combination. Please verify your details and try again.";
	}

	// Ignore useless generic placeholders so we fall back to our default
	const genericPlaceholders = new Set([
		'unknown error',
		'error',
		'internal server error',
		'server error',
		'bad request',
		'network error'
	]);
	const cleaned = messages
		.map((m) => String(m).trim())
		.filter(Boolean)
		.filter((m) => !genericPlaceholders.has(m.toLowerCase()));

	if (cleaned.length) {
		return cleaned.join(' ');
	}

	// Default fallback error message
	return "There seems to be an issue with the quote, please contact Achmea.";
};
